<template>
  <b-modal v-model="isActive" @close="$emit('update:modelValue', false)">
    <ld-modal-content title="OpenAI Settings">
      <div>
        <b-field v-if="patientId">
          <b-switch v-model="isPatientSpecific">For this patient only</b-switch>
        </b-field>
        <b-field label="Instructions">
          <b-input type="textarea" v-model="instructions" rows="6"></b-input>
        </b-field>
        <b-field label="Other Settings">
          <b-input type="textarea" v-model="settingsModel" rows="4"></b-input>
        </b-field>
      </div>
      <template #footer>
        <div class="is-centered">
          <button class="button is-text" @click="$emit('update:modelValue', false)">Cancel</button>
          <button class="button is-warning mr-2" @click="resetToDefaults">Reset to Global</button>
          <button class="button is-primary" @click="saveSettings">Save</button>
        </div>
      </template>
    </ld-modal-content>
  </b-modal>
</template>

<script>
import LdModalContent from "@/components/LdModalContent"

const DEFAULT_INSTRUCTIONS = `Do not contribute your own thoughts or content.
Do not interrupt. Wait silently until a speaker finishes before translating.
If someone speaks in a language other than English, translate exactly what they said into English once they finish speaking.
If someone explicitly says "Please translate to Punjabi," translate only their very next English sentence precisely into Punjabi.
Do not answer questions, provide additional explanations, or participate in any conversation.
Your role is entirely passive except for providing accurate, immediate translations.`

const DEFAULT_SETTINGS = {
  model: "gpt-4o-realtime-preview-2024-12-17",
  voice: "alloy"
}

export default {
  name: "OpenAISettingsModal",
  components: { LdModalContent },
  props: {
    modelValue: Boolean,
    patientId: Number
  },
  emits: ['update:modelValue', 'settings-saved'],
  computed: {
    isActive: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    settingsModel: {
      get() {
        return JSON.stringify(this.settings, null, 2)
      },
      set(val) {
        this.settings = JSON.parse(val)
      }
    }
  },
  data() {
    return {
      instructions: DEFAULT_INSTRUCTIONS,
      settings: DEFAULT_SETTINGS,
      isPatientSpecific: false
    }
  },
  methods: {
    loadSettings() {
      const patientSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS_PATIENT')
      const globalSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS')
      
      if (patientSettings) {
        const parsed = JSON.parse(patientSettings)
        if (parsed[this.patientId]) {
          this.settings = parsed[this.patientId].settings
          this.instructions = parsed[this.patientId].instructions
          this.isPatientSpecific = true
          return
        }
      }

      if (globalSettings) {
        const { settings, instructions } = JSON.parse(globalSettings)
        this.settings = settings
        this.instructions = instructions
        return
      }
    },
    resetToDefaults() {
      if (this.isPatientSpecific) {
        if (this.patientId) {
          const patientSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS_PATIENT')
          if (patientSettings) {
            const parsed = JSON.parse(patientSettings)
            delete parsed[this.patientId]
            localStorage.setItem('VIDEO_MEET_OPENAI_SETTINGS_PATIENT', JSON.stringify(parsed))
          }

          const globalSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS')
          if (globalSettings) {
            const parsed = JSON.parse(globalSettings)
            this.settings = parsed.settings
            this.instructions = parsed.instructions
            this.$emit('settings-saved', { settings: parsed.settings, instructions: parsed.instructions })
          } else {
            this.settings = DEFAULT_SETTINGS
            this.instructions = DEFAULT_INSTRUCTIONS
            this.$emit('settings-saved', { settings: DEFAULT_SETTINGS, instructions: DEFAULT_INSTRUCTIONS })
          }
        }
      } else {
        localStorage.setItem('VIDEO_MEET_OPENAI_SETTINGS', JSON.stringify({
          settings: DEFAULT_SETTINGS,
          instructions: DEFAULT_INSTRUCTIONS
        }))
        this.settings = DEFAULT_SETTINGS
        this.instructions = DEFAULT_INSTRUCTIONS
        this.$emit('settings-saved', { settings: DEFAULT_SETTINGS, instructions: DEFAULT_INSTRUCTIONS })
      }

      this.isPatientSpecific = false
      this.isActive = false
    },
    saveSettings() {
      const settings = JSON.parse(this.settingsModel)
      
      if (this.patientId && this.isPatientSpecific) {
        const patientSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS_PATIENT') || '{}'
        const parsed = JSON.parse(patientSettings)
        parsed[this.patientId] = { settings, instructions: this.instructions }
        localStorage.setItem('VIDEO_MEET_OPENAI_SETTINGS_PATIENT', JSON.stringify(parsed))
      } else {
        localStorage.setItem('VIDEO_MEET_OPENAI_SETTINGS', JSON.stringify({
          settings,
          instructions: this.instructions
        }))
      }

      this.$emit('settings-saved', { settings, instructions: this.instructions })
      this.isActive = false
    },
    emitInitialSettings() {
      const patientId = this.patientId
      if (patientId) {
        const patientSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS_PATIENT')
        if (patientSettings) {
          const parsed = JSON.parse(patientSettings)
          if (parsed[patientId]) {
            this.$emit('settings-saved', { 
              settings: parsed[patientId].settings, 
              instructions: parsed[patientId].instructions 
            })
            return
          }
        }
      }

      const globalSettings = localStorage.getItem('VIDEO_MEET_OPENAI_SETTINGS')
      if (globalSettings) {
        const { settings, instructions } = JSON.parse(globalSettings)
        this.$emit('settings-saved', { settings, instructions })
      } else {
        this.$emit('settings-saved', { 
          settings: DEFAULT_SETTINGS, 
          instructions: DEFAULT_INSTRUCTIONS 
        })
      }
    }
  },
  mounted() {
    this.loadSettings()
    this.emitInitialSettings()
  }
}
</script> 